import { Injectable } from '@angular/core';
import { Observable, take } from 'rxjs';

import { PersonalizationHistory, PersonalizationHome } from '../common/PersonalizationHome';
import { ApiServiceBase } from './ApiServiceBase';

/**
 * #94 / backend #651: the personalized landing page and the two signal recorders behind it.
 *
 * <b>Scope.</b> Every route works on the CALLER's own profile, resolved from the token — a
 * profile id is never sent from here. Same rule as {@link BookmarkService}.
 *
 * <b>Auth.</b> The whole controller is `[Authorize]` and a token without a profile gets 403,
 * so callers gate on a profile the way the bookmark icon does; `/` and `/catalog-item/:id`
 * are public routes and an anonymous visitor must not be sent into a 401.
 *
 * <b>Consent.</b> Enforced in the backend, not here: while `PersonalizationEnabled` is off
 * nothing is recorded and the derived sections come back empty. Both recorders answer 204
 * either way, which is why {@link recordVisit} and {@link recordSearch} have no branch around
 * them — and why they are fire-and-forget: a browsing user must never see an error toast for
 * a signal that failed to record.
 */
@Injectable({
  providedIn: 'root',
})
export class PersonalizationService extends ApiServiceBase {
  protected readonly path = 'personalization';

  /**
   * All three landing-page sections in one call. `take` is per section; omitted, the backend
   * uses its own default (12).
   */
  public getHome(take?: number): Observable<PersonalizationHome> {
    return this.http
      .get<PersonalizationHome>(
        this.getUrl() + '/home',
        take === undefined ? {} : { params: { take } },
      );
  }

  /** Records that the user opened a listing. Called from the detail view; 204 always. */
  public recordVisit(catalogSkuId: string): Observable<unknown> {
    return this.http
      .post(`${this.getUrl()}/visits/${catalogSkuId}`, null)
      .pipe(take(1));
  }

  /**
   * Records a search the user actually RAN. Never call this per keystroke of the search
   * field — the history would fill up with every prefix of every word (backend #651).
   */
  public recordSearch(term: string, productCategoryId?: string): Observable<unknown> {
    return this.http
      .post(`${this.getUrl()}/searches`, { term, productCategoryId: productCategoryId ?? null })
      .pipe(take(1));
  }

  /** The raw stored history — the GDPR Art. 15 answer behind „Gespeicherte Daten anzeigen". */
  public getHistory(): Observable<PersonalizationHistory> {
    return this.http
      .get<PersonalizationHistory>(this.getUrl() + '/history')
      .pipe(take(1));
  }

  /**
   * Deletes the stored history (Art. 17) and leaves personalization ON — this is „forget
   * what you know about me", not „stop". Switching the setting off deletes the history too,
   * but through the setting write, so the two can never disagree.
   */
  public deleteHistory(): Observable<unknown> {
    return this.http.delete(this.getUrl() + '/history').pipe(take(1));
  }
}
